<template>
  <div class="checkout-step-four">
    <!-- form part 1 -->
    <transition name="slide-fade">
      <form
        @submit.prevent="next('form-1')"
        data-vv-scope="form-1"
        v-if="!steps[4].formPart1Passed"
        novalidate
      >
        <section class="form-section">
          <div class="checkout-step__header">
            <div class="row align-items-end">
              <div class="col-12 col-md-9">
                <h3 class="checkout-step__headline">
                  {{ $t('pages.checkoutStepFour.formPart1.title') }}
                </h3>
                <p class="checkout-step__copy">{{ $t('pages.checkoutStepFour.formPart1.copy') }}</p>
              </div>
              <div class="col-12 col-md-3">
                <small class="checkout-step__copy checkout-step__copy--small">
                  {{ $t('generic.labels.required') }}
                </small>
              </div>
            </div>
          </div>
          <div class="form-row">
            <div class="col-12 col-sm-6">
              <div class="form-group" :class="{ error: errors.has('form-1.billing_name') }">
                <label class="form-group__label form-group__label--required" for="billing_name">
                  {{ $t('pages.checkoutStepFour.formPart1.labels.name') }}
                </label>
                <input
                  class="form-group__input"
                  type="text"
                  id="billing_name"
                  name="billing_name"
                  :value="billing.name"
                  @input="setBilling({ k: 'name', v: $event.target.value })"
                  @change="setFirstTraveller('first_name', $event.target.value)"
                  maxlength="254"
                  v-validate="{ required: true, max: 254 }"
                  autocomplete="billing given-name"
                />
                <small class="form-group__small" v-if="errors.has('form-1.billing_name')">
                  {{ $t('generic.form.errors.name') }}
                </small>
              </div>
            </div>
            <div class="col-12 col-sm-6">
              <div class="form-group" :class="{ error: errors.has('form-1.billing_lastname') }">
                <label class="form-group__label form-group__label--required" for="billing_lastname">
                  {{ $t('pages.checkoutStepFour.formPart1.labels.surname') }}
                </label>
                <input
                  class="form-group__input"
                  type="text"
                  id="billing_lastname"
                  name="billing_lastname"
                  :value="billing.lastname"
                  @input="setBilling({ k: 'lastname', v: $event.target.value })"
                  @change="setFirstTraveller('last_name', $event.target.value)"
                  maxlength="254"
                  v-validate="{ required: true, max: 254 }"
                  autocomplete="billing family-name"
                />
                <small class="form-group__small" v-if="errors.has('form-1.billing_lastname')">
                  {{ $t('generic.form.errors.lastname') }}
                </small>
              </div>
            </div>
            <div class="col-12 col-sm-6">
              <div class="form-group" :class="{ error: errors.has('form-1.billing_mail') }">
                <label class="form-group__label form-group__label--required" for="billing_mail">
                  {{ $t('pages.checkoutStepFour.formPart1.labels.mail') }}
                </label>
                <input
                  class="form-group__input"
                  type="email"
                  id="billing_mail"
                  name="billing_mail"
                  data-vv-name="billing_mail"
                  :value="billing.mail"
                  data-vv-validate-on="input"
                  @input="
                    setBilling({ k: 'mail', v: ($event.target.value || '').trim().toLowerCase() })
                  "
                  ref="mail"
                  v-validate="{ required: true, email: true }"
                  autocomplete="billing email"
                />
                <small class="form-group__small" v-if="errors.has('form-1.billing_mail')">
                  {{ $t('generic.form.errors.mail') }}
                </small>
              </div>
            </div>
            <div class="col-12 col-sm-6">
              <div
                class="form-group"
                :class="{
                  error:
                    fields['$form-1'] &&
                    fields['$form-1'].billing_mail_repeat.touched &&
                    errors.has('form-1.billing_mail_repeat'),
                }"
              >
                <label
                  class="form-group__label form-group__label--required"
                  for="billing_mail_repeat"
                >
                  {{ $t('pages.checkoutStepFour.formPart1.labels.mail_repeat') }}
                </label>
                <input
                  class="form-group__input"
                  type="email"
                  id="billing_mail_repeat"
                  name="billing_mail_repeat"
                  data-vv-name="billing_mail_repeat"
                  :value="billing.mail_repeat"
                  data-vv-validate-on="input"
                  @input="
                    setBilling({
                      k: 'mail_repeat',
                      v: ($event.target.value || '').trim().toLowerCase(),
                    })
                  "
                  v-validate="{ required: true, is: normalizedPrimaryMail }"
                  autocomplete="billing email"
                />
                <small
                  class="form-group__small"
                  v-if="
                    fields['$form-1'] &&
                      fields['$form-1'].billing_mail_repeat.touched &&
                      errors.has('form-1.billing_mail_repeat')
                  "
                >
                  {{ $t('generic.form.errors.mail_repeat') }}
                </small>
              </div>
            </div>
            <div class="col-12 col-sm-6">
              <div class="form-group">
                <label class="form-group__label" for="billing_phone">
                  {{ $t('pages.checkoutStepFour.formPart1.labels.phone') }}
                </label>
                <input
                  class="form-group__input"
                  type="tel"
                  id="billing_phone"
                  name="billing_phone"
                  :value="billing.phone"
                  maxlength="128"
                  @input="setBilling({ k: 'phone', v: $event.target.value })"
                  autocomplete="billing tel-national"
                />
              </div>
            </div>
          </div>
        </section>

        <section class="form-section">
          <div class="checkout-step__header">
            <div class="row align-items-end">
              <div class="col-12 col-md-9">
                <h3 class="checkout-step__headline">
                  {{ $t('pages.checkoutStepFour.formPart1.traveller.title') }}
                </h3>
                <p class="checkout-step__copy">
                  {{ $t('pages.checkoutStepFour.formPart1.traveller.copy') }}
                </p>
              </div>
            </div>
          </div>
          <div
            class="form-row"
            v-for="(traveller, index) in travellers"
            :key="index"
            v-if="traveller"
          >
            <div :class="inputTravellerNationality ? 'col-12 col-sm-6' : 'col-12 col-sm-4'">
              <div
                class="form-group"
                :class="{ error: errors.has(`form-1.traveller_${index}_first_name`) }"
              >
                <label
                  class="form-group__label form-group__label--required"
                  :for="`form-1.traveller_${index}_first_name`"
                >
                  {{ $t('pages.checkoutStepFour.formPart1.labels.name') }}
                </label>
                <input
                  class="form-group__input"
                  type="text"
                  :id="`traveller_${index}_first_name`"
                  :name="`traveller_${index}_first_name`"
                  :value="traveller.first_name"
                  maxlength="254"
                  v-validate="{ required: true, max: 254 }"
                  @input="setTraveller({ k: 'first_name', i: index, v: $event.target.value })"
                />
                <small
                  class="form-group__small"
                  v-if="errors.has(`form-1.traveller_${index}_first_name`)"
                >
                  {{ $t('generic.form.errors.name') }}
                </small>
              </div>
            </div>
            <div :class="inputTravellerNationality ? 'col-12 col-sm-6' : 'col-12 col-sm-4'">
              <div
                class="form-group"
                :class="{ error: errors.has(`form-1.traveller_${index}_last_name`) }"
              >
                <label
                  class="form-group__label form-group__label--required"
                  :for="`traveller_${index}_last_name`"
                >
                  {{ $t('pages.checkoutStepFour.formPart1.labels.surname') }}
                </label>
                <input
                  class="form-group__input"
                  type="text"
                  :id="`traveller_${index}_last_name`"
                  :name="`traveller_${index}_last_name`"
                  :value="traveller.last_name"
                  maxlength="254"
                  v-validate="{ required: true, max: 254 }"
                  @input="setTraveller({ k: 'last_name', i: index, v: $event.target.value })"
                />
                <small
                  class="form-group__small"
                  v-if="errors.has(`form-1.traveller_${index}_last_name`)"
                >
                  {{ $t('generic.form.errors.lastname') }}
                </small>
              </div>
            </div>
            <div :class="inputTravellerNationality ? 'col-12 col-sm-6' : 'col-12 col-sm-4'">
              <div
                class="form-group"
                :class="{ error: errors.has(`form-1.traveller_${index}_birthdate`) }"
              >
                <label
                  class="form-group__label form-group__label--required"
                  :for="`traveller_${index}_birthdate`"
                >
                  {{ $t('pages.checkoutStepFour.formPart1.labels.birthDate') }}
                  <span
                    class="age-on-arrival"
                    v-if="
                      traveller.birthdate &&
                        dates &&
                        dates.length &&
                        ageOnArrival(traveller.birthdate) !== null
                    "
                  >
                    ({{ ageOnArrival(traveller.birthdate) }} {{ $t('generic.yearsShort')
                    }}<template v-if="personNameForBirthdate(traveller.birthdate)"
                      >, {{ personNameForBirthdate(traveller.birthdate) }}</template
                    >)
                  </span>
                </label>
                <ci-datepicker
                  :value="traveller.birthdate"
                  :config="pickerConfig"
                  :open="getDatepickerOpen(index)"
                  :locale="langSlug"
                  :ref="`birthdate_${index}`"
                  @toggle-open="setDatepickerOpen(index, $event)"
                  @blur.native="onBirthdateBlur(index)"
                  @input="
                    setTraveller({ k: 'birthdate', i: index, v: $event })
                    _debouncedCheckBirthdates()
                  "
                  :inputId="`traveller_${index}_birthdate`"
                  :name="`traveller_${index}_birthdate`"
                  v-validate="'required'"
                >
                </ci-datepicker>
                <small
                  class="form-group__small"
                  v-if="errors.has(`form-1.traveller_${index}_birthdate`)"
                >
                  {{ $t('generic.form.errors.birthdate') }}
                </small>
              </div>
            </div>

            <!-- todo gk -->
            <div class="col-12 col-sm-6" v-if="inputTravellerNationality">
              <div
                class="form-group"
                :class="{ error: errors.has(`form-1.traveller_${index}_nationality`) }"
              >
                <label
                  class="form-group__label form-group__label--required"
                  :for="`traveller_${index}_nationality`"
                >
                  {{ $t('pages.checkoutStepFour.formPart1.labels.nationality') }}
                </label>
                <multiselect
                  :id="`traveller_${index}_nationality`"
                  :value="traveller.nationality"
                  :options="countries"
                  :searchable="true"
                  :close-on-select="true"
                  :show-labels="false"
                  :allow-empty="!isNationalityRequired"
                  :placeholder="$t('generic.labels.choose')"
                  :name="`traveller_${index}_nationality`"
                  @input="setTraveller({ k: 'nationality', i: index, v: $event })"
                  track-by="label"
                  label="label"
                  v-validate="{ required: isNationalityRequired }"
                  autocomplete="billing country"
                >
                  <template slot="singleLabel" slot-scope="{ option }">{{ option.label }}</template>
                  <template slot="noResult">{{ $t('generic.labels.noResult') }}</template>
                </multiselect>
                <small
                  class="form-group__small"
                  v-if="errors.has(`form-1.traveller_${index}_nationality`)"
                >
                  {{ $t('generic.form.errors.country') }}
                </small>
              </div>
            </div>
            <!-- todo gk -->

            <div class="col-12" v-if="inputTravellerNationality">
              <hr class="traveller-separator"/>
            </div>
          </div>
          <div v-if="birthdayError">
            <p class="birthday-error">
              {{ birthdayError }}
            </p>
          </div>
        </section>

        <section class="form-section" v-if="queryVehicleInfos">
          <div class="checkout-step__header">
            <div class="row align-items-end">
              <div class="col-12 col-md-9">
                <h3 class="checkout-step__headline">
                  {{ $t('pages.checkoutStepFour.formPart1.car.title') }}
                </h3>
                <p
                  class="checkout-step__copy"
                  v-if="
                    selectedCampsite.input_vehicle_info != 'n' &&
                      selectedCampsite.input_vehicle_size != 'n'
                  "
                >
                  {{ $t('pages.checkoutStepFour.formPart1.car.copyMeasurementsAndPlate') }}
                </p>
                <p class="checkout-step__copy" v-else-if="showVehicleInfo">
                  {{ $t('pages.checkoutStepFour.formPart1.car.copyPlate') }}
                </p>
                <p class="checkout-step__copy" v-else-if="showCarDetails">
                  {{ $t('pages.checkoutStepFour.formPart1.car.copyMeasurements') }}
                </p>

                <span class="checkout-step__copy" v-if="selectedCampsite.input_vehicle_info === 'o'">
                  {{ $t('pages.checkoutStepFour.formPart1.car.copy') }}
                </span>
                <span class="checkout-step__copy" v-if="selectedCampsite.input_vehicle_size === 'o'">
                  {{ $t('pages.checkoutStepFour.formPart1.car.size_optional') }}
                </span>
              </div>
            </div>
          </div>
          <div class="form-row">
            <div class="col-12 col-sm-4" v-if="showVehicleInfo">
              <div class="form-group" :class="{ error: errors.has('form-1.car_license_plate') }">
                <div>
                  <label
                    :class="[
                      'form-group__label',
                      {
                        'form-group__label--required': selectedCampsite.input_vehicle_info === 'm',
                      },
                    ]"
                    for="car_license_plate"
                  >
                    {{ $t('pages.checkoutStepFour.formPart1.car.license_plate') }}
                  </label>
                  <input
                    class="form-group__input"
                    type="text"
                    id="car_license_plate"
                    name="car_license_plate"
                    :value="car.license_plate"
                    maxlength="32"
                    v-validate="{ required: selectedCampsite.input_vehicle_info === 'm', max: 32 }"
                    @input="setCar({ k: 'license_plate', v: $event.target.value })"
                  />
                </div>
                <small class="form-group__small" v-if="errors.has('form-1.car_license_plate')">
                  <span>{{ $t('generic.form.errors.car.license_plate') }}</span>
                </small>
              </div>
            </div>
            <div class="col-12 col-sm-4" v-if="showCarDetails">
              <div class="form-group" :class="{ error: errors.has('form-1.car_length') }">
                <label
                  :class="[
                    'form-group__label',
                    { 'form-group__label--required': selectedCampsite.input_vehicle_size === 'm' },
                  ]"
                  for="car_length"
                >
                  {{ $t('pages.checkoutStepFour.formPart1.car.kfzLength') }}
                </label>
                <div class="suffix-wrapper">
                  <input
                    class="form-group__input"
                    type="number"
                    id="car_length"
                    name="car_length"
                    min="1"
                    max="50"
                    :value="car.length"
                    v-validate="{
                      required: selectedCampsite.input_vehicle_size === 'm',
                      min_value: 1,
                      max_value: 50,
                    }"
                    @input="setCar({ k: 'length', v: $event.target.value })"
                  />
                </div>
                <small class="form-group__small" v-if="errors.has('form-1.car_length')">
                  <span v-if="errors.firstByRule('form-1.car_length', 'min_value')">{{
                    $t('generic.form.errors.car.lengthMin', { min: 1 })
                  }}</span>
                  <span v-else-if="errors.firstByRule('form-1.car_length', 'max_value')">{{
                    $t('generic.form.errors.car.lengthMax', { max: 50 })
                  }}</span>
                  <span v-else>{{ $t('generic.form.errors.car.length') }}</span>
                </small>
              </div>
            </div>
            <div class="col-12 col-sm-4" v-if="showCarDetails">
              <div class="form-group" :class="{ error: errors.has('form-1.car_width') }">
                <label
                  :class="[
                    'form-group__label',
                    { 'form-group__label--required': selectedCampsite.input_vehicle_size === 'm' },
                  ]"
                  for="car_width"
                >
                  {{ $t('pages.checkoutStepFour.formPart1.car.kfzWidth') }}
                </label>
                <div class="suffix-wrapper">
                  <input
                    class="form-group__input"
                    type="number"
                    id="car_width"
                    name="car_width"
                    min="1"
                    max="10"
                    :value="car.width"
                    v-validate="{
                      required: selectedCampsite.input_vehicle_size === 'm',
                      min_value: 1,
                      max_value: 10,
                    }"
                    @input="setCar({ k: 'width', v: $event.target.value })"
                  />
                </div>
                <small class="form-group__small" v-if="errors.has('form-1.car_width')">
                  <span v-if="errors.firstByRule('form-1.car_width', 'min_value')">{{
                    $t('generic.form.errors.car.widthMin', { min: 1 })
                  }}</span>
                  <span v-else-if="errors.firstByRule('form-1.car_width', 'max_value')">{{
                    $t('generic.form.errors.car.widthMax', { max: 10 })
                  }}</span>
                  <span v-else>{{ $t('generic.form.errors.car.width') }}</span>
                </small>
              </div>
            </div>
          </div>
        </section>

        <CiGenericNavigation
          @clickSecondary="back('form-1')"
          @clickPrimary="next('form-1')"
          :ciDisabledPrimary="!!birthdayError"
        >
          <template slot="secondary">
            <iconArrowBlack class="icon icon--animated"></iconArrowBlack>
            <span>{{ $t('generic.buttons.stepBack') }}</span>
          </template>
          <template slot="primary">
            <span>{{ $t('generic.buttons.next') }}</span>
            <iconArrowWhite class="icon icon--animated"></iconArrowWhite>
          </template>
        </CiGenericNavigation>
      </form>
    </transition>

    <!-- form part 2 -->
    <transition name="slide-fade">
      <form data-vv-scope="form-2" v-if="steps[4].formPart1Passed" novalidate>
        <section class="form-section">
          <div class="checkout-step__header">
            <div class="row align-items-end">
              <div class="col-12 col-md-9">
                <h3 class="checkout-step__headline">
                  {{ $t('pages.checkoutStepFour.formPart2.title') }}
                </h3>

                <p class="checkout-step__copy">
                  {{ $t('pages.checkoutStepFour.formPart2.copy') }}
                </p>
              </div>
              <div class="col-12 col-md-3">
                <small class="checkout-step__copy checkout-step__copy--small">
                  {{ $t('generic.labels.required') }}
                </small>
              </div>
            </div>
          </div>
          <div class="form-row">
            <div class="col-12 col-sm-6">
              <div class="form-group" :class="{ error: errors.has('form-2.billing_street') }">
                <label class="form-group__label form-group__label--required" for="billing_street">
                  {{ $t('pages.checkoutStepFour.formPart2.labels.street') }}
                </label>
                <input
                  class="form-group__input"
                  type="text"
                  id="billing_street"
                  name="billing_street"
                  :value="billing.street"
                  @input="setBilling({ k: 'street', v: $event.target.value })"
                  maxlength="254"
                  v-validate="{ required: true, max: 254 }"
                  autocomplete="billing address-line1"
                />
                <small class="form-group__small" v-if="errors.has('form-2.billing_street')">
                  {{ $t('generic.form.errors.street') }}
                </small>
              </div>
            </div>
            <div class="col-12 col-sm-6">
              <div class="form-group">
                <label class="form-group__label" for="billing_street_additional">
                  {{ $t('pages.checkoutStepFour.formPart2.labels.streetAdditional') }}
                </label>
                <input
                  class="form-group__input"
                  type="text"
                  id="billing_street_additional"
                  name="billing_street_additional"
                  maxlength="254"
                  :value="billing.street_additional"
                  @input="setBilling({ k: 'street_additional', v: $event.target.value })"
                  autocomplete="billing address-line2"
                />
              </div>
            </div>
            <div class="col-12 col-sm-2">
              <div class="form-group" :class="{ error: errors.has('form-2.billing_zip') }">
                <label class="form-group__label form-group__label--required" for="billing_zip">
                  {{ $t('pages.checkoutStepFour.formPart2.labels.zip') }}
                </label>
                <input
                  class="form-group__input"
                  type="text"
                  id="billing_zip"
                  name="billing_zip"
                  :value="billing.zip"
                  @input="setBilling({ k: 'zip', v: $event.target.value })"
                  maxlength="32"
                  v-validate="{ required: true, max: 32 }"
                  autocomplete="billing postal-code"
                />
                <small class="form-group__small" v-if="errors.has('form-2.billing_zip')">
                  {{ $t('generic.form.errors.zip') }}
                </small>
              </div>
            </div>
            <div class="col-12 col-sm-4">
              <div class="form-group" :class="{ error: errors.has('form-2.billing_city') }">
                <label class="form-group__label form-group__label--required" for="billing_city">
                  {{ $t('pages.checkoutStepFour.formPart2.labels.city') }}
                </label>
                <input
                  class="form-group__input"
                  type="text"
                  id="billing_city"
                  name="billing_city"
                  :value="billing.city"
                  maxlength="254"
                  @input="setBilling({ k: 'city', v: $event.target.value })"
                  v-validate="{ required: true, max: 254 }"
                  autocomplete="billing address-level2"
                />
                <small class="form-group__small" v-if="errors.has('form-2.billing_city')">
                  {{ $t('generic.form.errors.city') }}
                </small>
              </div>
            </div>
            <div class="col-12 col-sm-6">
              <div class="form-group" :class="{ error: errors.has('form-2.billing_country') }">
                <label class="form-group__label form-group__label--required" for="billing_country">
                  {{ $t('pages.checkoutStepFour.formPart2.labels.country') }}
                </label>
                <multiselect
                  id="billing_country"
                  :value="billing.country"
                  :options="countries"
                  :searchable="true"
                  :close-on-select="true"
                  :show-labels="false"
                  :allow-empty="false"
                  :placeholder="$t('generic.labels.choose')"
                  name="billing_country"
                  @input="setCountry"
                  track-by="label"
                  label="label"
                  v-validate="'required'"
                  autocomplete="billing country"
                >
                  <template slot="singleLabel" slot-scope="{ option }">{{ option.label }}</template>
                  <template slot="noResult">{{ $t('generic.labels.noResult') }}</template>
                </multiselect>
                <small class="form-group__small" v-if="errors.has('form-2.billing_country')">
                  {{ $t('generic.form.errors.country') }}
                </small>
              </div>
            </div>
          </div>
        </section>

        <section class="form-section">
          <div class="checkout-step__header">
            <div class="row align-items-end">
              <div class="col-12 col-md-9">
                <h3 class="checkout-step__headline">
                  {{ $t('pages.checkoutStepFour.formPart2.campsite.title') }}
                </h3>
                <p class="checkout-step__copy">
                  {{ $t('pages.checkoutStepFour.formPart2.campsite.copy') }}
                </p>
              </div>
            </div>
          </div>
          <div class="form-row">
            <div class="col-12">
              <div class="form-group">
                <label class="form-group__label" for="billing_message">
                  {{ $t('generic.labels.message') }}
                </label>
                <textarea
                  class="form-group__input"
                  id="billing_message"
                  rows="8"
                  name="billing_message"
                  :value="billing.message"
                  @input="setBilling({ k: 'message', v: $event.target.value })"
                >
                </textarea>
              </div>
            </div>
          </div>
        </section>

        <CiGenericNavigation @clickSecondary="back('form-2')" @clickPrimary="next('form-2')">
          <template slot="secondary">
            <iconArrowBlack class="icon icon--animated"></iconArrowBlack>
            <span>{{ $t('generic.buttons.stepBack') }}</span>
          </template>
          <template slot="primary">
            <span>{{ $t('generic.buttons.nextStep') }}</span>
            <iconArrowWhite class="icon icon--animated"></iconArrowWhite>
          </template>
        </CiGenericNavigation>
      </form>
    </transition>
  </div>
</template>

<script>
import debounce from 'lodash/debounce'
import dayjs from 'dayjs'
import CiDatepicker from '@/vendor/ci-datepicker'
import { mapGetters, mapActions } from 'vuex'
import { Api } from '@/api/api'
import { CiSelectionBox, CiGenericNavigation } from '@/components/index'
import iconArrowBlack from '@/assets/icons/arrow-black.svg'
import iconArrowWhite from '@/assets/icons/arrow-white.svg'

export default {
  name: 'CheckoutStepFour',
  components: {
    CiDatepicker,
    CiGenericNavigation,
    CiSelectionBox,
    iconArrowBlack,
    iconArrowWhite,
  },
  data() {
    return {
      pickerConfig: {
        range: false,
        inline: false,
        closeAfterSelect: true,
        minDate: new Date(1900, 0, 1),
        maxDate: new Date(),
        dateFormat: this.$t('generic.date.format'),
        i18n: this.datepickerI18n(),
        birthdateMode: true,
        breakpoint: 10000,
        align: 'right',
        pickerWidthPixelOffset: -1,
      },
      datepickersOpen: [],
      birthdayError: null,
      allowClose: {},
    }
  },
  mounted() {
    // Click-Listener für den ganzen Body
    document.addEventListener('click', this.handleDatepickerClick, true)
    // Focus-Listener, um Schließen bei Fokuswechsel (z.B. via TAB) sicherzustellen
    document.addEventListener('focusin', this.handleGlobalFocusIn, true)
    // Keydown-Listener, um ESC (und TAB als Fallback) zu behandeln
    document.addEventListener('keydown', this.handleGlobalKeydown, true)

    // Ensure Vee-Validate knows about programmatically prefilled values (e.g., from draftbooking)
    this.$nextTick(() => {
      try {
        if (this.billing && (this.billing.mail || this.billing.mail_repeat)) {
          // Trigger validation to sync internal values used by `confirmed` rule
          this.$validator.validate('form-1.billing_mail')
          this.$validator.validate('form-1.billing_mail_repeat')
        }
      } catch (e) {
        // no-op
      }
    })
  },

  beforeDestroy() {
    document.removeEventListener('click', this.handleDatepickerClick, true)
    document.removeEventListener('focusin', this.handleGlobalFocusIn, true)
    document.removeEventListener('keydown', this.handleGlobalKeydown, true)
  },

  // created merged below
  computed: {
    ...mapGetters('application', {
      activeStep: 'activeStep',
      steps: 'steps',
      langSlug: 'langSlug',
      showBookingDetails: 'showBookingDetails',
    }),

    ...mapGetters('booking', {
      car: 'car',
      billing: 'billing',
      selectedCampsite: 'selectedCampsite',
      travellers: 'travellers',
      dates: 'dates',
      selectedPersons: 'selectedPersons',
    }),

    ...mapGetters('campground', {
      campground: 'campground',
      countries: 'countries',
    }),

    queryVehicleInfos() {
      console.log(
        'gk-debug',
        'queryVehicleInfos',
        this.selectedCampsite.input_vehicle_info,
        this.selectedCampsite.input_vehicle_size
      )
      if (!this.selectedCampsite) {
        return true
      }
      return (
        this.selectedCampsite.input_vehicle_info !== 'n' ||
        this.selectedCampsite.input_vehicle_size !== 'n'
      )
    },

    showCarDetails() {
      console.log('gk-debug', 'showCarDetails', this.selectedCampsite)
      if (!this.selectedCampsite) {
        return true
      }
      return this.selectedCampsite.input_vehicle_size !== 'n'
    },

    showVehicleInfo() {
      console.log('gk-debug', 'showCarDetails', this.selectedCampsite)
      if (!this.selectedCampsite) {
        return true
      }
      return this.selectedCampsite.input_vehicle_info !== 'n'
    },

    inputTravellerNationality() {
      if (!this.selectedCampsite) return false
      const config = this.campground.checkout_config
      return config ? ['o', 'm'].includes(config.input_guests_nationality) : false
    },

    isNationalityRequired() {
      const config = this.campground && this.campground.checkout_config
      return config ? config.input_guests_nationality === 'm' : false
    },

    formValid() {
      return this.errors.items.length <= 0
    },

    // Always compare against the normalized primary mail value
    normalizedPrimaryMail() {
      const m = this.billing && this.billing.mail ? this.billing.mail : ''
      return (m || '')
        .toString()
        .trim()
        .toLowerCase()
    },
  },
  methods: {
    ...mapActions('application', {
      setApplicationStepStatus: 'setStepStatus',
      toggleBookingDetails: 'toggleBookingDetails',
    }),

    ...mapActions('booking', {
      setBilling: 'setBilling',
      setCar: 'setCar',
      setTraveller: 'setTraveller',
    }),

    // Safely resolve a ref (component instance or element) to a DOM Element that supports contains()
    _resolveBirthdateEl(refVal) {
      try {
        // If ref is an array (shouldn't be with indexed ref names, but guard anyway)
        if (Array.isArray(refVal)) {
          refVal = refVal.find(Boolean) || refVal[0]
        }
        let el = refVal && (refVal.$el || refVal)

        // If it's a text/comment node, bubble up to nearest element
        while (el && el.nodeType && el.nodeType !== 1 /* ELEMENT_NODE */) {
          el = el.parentNode
        }
        return el || null
      } catch (e) {
        return null
      }
    },

    _isInsideEl(el, target) {
      if (!el || !target) return false
      if (el === target) return true
      // Some elements (non-HTMLElement nodes) may not have contains
      if (typeof el.contains === 'function') {
        return el.contains(target)
      }
      // Fallback: walk up from target
      try {
        let cur = target
        while (cur) {
          if (cur === el) return true
          cur = cur.parentNode
        }
      } catch (e) {
        // ignore
      }
      return false
    },

    handleDatepickerClick(event) {
      const target = event.target

      // Jahr-Klick: <a class="y-1979">
      if (target.tagName === 'A' && target.className && target.className.startsWith('y-')) {
        // Setze allowClose auf false für alle Picker
        Object.keys(this.datepickersOpen).forEach(idx => {
          this.$set(this.allowClose, idx, false)
        })
        return
      }

      // Monat-Header-Klick: <a>Februar 1982</a> (kein className)
      if (target.tagName === 'A' && !target.className) {
        Object.keys(this.datepickersOpen).forEach(idx => {
          this.$set(this.allowClose, idx, false)
        })
        return
      }

      // Tag-Klick: SPAN in TD
      const dayCell = target.closest('td')
      if (dayCell && !dayCell.classList.contains('is-disabled')) {
        Object.keys(this.datepickersOpen).forEach(idx => {
          this.$set(this.allowClose, idx, true)
        })
        return
      }

      // Klick außerhalb jeglicher Datepicker-Komponente: alle offenen schließen
      Object.keys(this.datepickersOpen).forEach(idx => {
        if (this.datepickersOpen[idx]) {
          const ref = this.$refs[`birthdate_${idx}`]
          const el = this._resolveBirthdateEl(ref)
          if (!this._isInsideEl(el, target)) {
            this.$set(this.allowClose, idx, true)
            this.setDatepickerOpen(idx, false)
          }
        }
      })
    },
    handleGlobalFocusIn(event) {
      const target = event.target
      // Wenn Fokus in ein Element außerhalb des aktiven Datepickers wechselt, schließen
      Object.keys(this.datepickersOpen).forEach(idx => {
        if (this.datepickersOpen[idx]) {
          const ref = this.$refs[`birthdate_${idx}`]
          const el = this._resolveBirthdateEl(ref)
          if (!this._isInsideEl(el, target)) {
            this.$set(this.allowClose, idx, true)
            this.setDatepickerOpen(idx, false)
          }
        }
      })
    },
    handleGlobalKeydown(event) {
      const key = event.key || event.code
      // ESC schließt alle geöffneten Datepicker
      if (key === 'Escape' || key === 'Esc') {
        // Verhindert Browser-Defaults, die den Fokus „verlieren“ lassen könnten
        if (typeof event.preventDefault === 'function') event.preventDefault()

        // Fokustarget merken (der Input des geöffneten Geburtstagsfelds)
        let focusEl = null

        Object.keys(this.datepickersOpen).forEach(idx => {
          if (this.datepickersOpen[idx]) {
            this.$set(this.allowClose, idx, true)

            // Versuche den eigentlichen Input zu finden
            const ref = this.$refs[`birthdate_${idx}`]
            const el = this._resolveBirthdateEl(ref)
            let input = null
            if (el && typeof el.querySelector === 'function') {
              input = el.querySelector('input')
            }
            // Fallback über bekannte ID
            if (!input) {
              input = document.getElementById(`traveller_${idx}_birthdate`)
            }
            if (!focusEl && input && typeof input.focus === 'function') {
              focusEl = input
            }

            this.setDatepickerOpen(idx, false)
          }
        })

        // Nach dem Schließen Fokus zuverlässig wieder auf den Input setzen
        this.$nextTick(() => {
          if (focusEl && typeof focusEl.focus === 'function') {
            try {
              focusEl.focus()
            } catch (e) {
              /* ignore */
            }
          }
        })
        return
      }

      // TAB: nach Fokuswechsel prüfen und ggf. schließen
      if (key === 'Tab') {
        // Warten, bis der Fokus umgesetzt wurde
        this.$nextTick(() => {
          const active = document.activeElement
          Object.keys(this.datepickersOpen).forEach(idx => {
            if (this.datepickersOpen[idx]) {
              const ref = this.$refs[`birthdate_${idx}`]
              const el = this._resolveBirthdateEl(ref)
              if (!this._isInsideEl(el, active)) {
                this.$set(this.allowClose, idx, true)
                this.setDatepickerOpen(idx, false)
              }
            }
          })
        })
      }
    },
    async checkBirthdates() {
      let isBirthdateSet = true
      const url = `check-booking/${this.campground.slug}`
      const guests = Object.values(this.travellers).map(traveller => {
        const guest = {
          ...traveller,
        }

        if (traveller.birthdate) {
          guest.birthdate = dayjs(traveller.birthdate).format('YYYY-MM-DD')
        } else {
          isBirthdateSet = false
        }

        return guest
      })

      if (!isBirthdateSet) {
        return
      }

      const data = {
        campsites: [
          {
            id: this.selectedCampsite.id,
            amount: this.selectedCampsite.amount,
            persons: this.selectedPersons,
          },
        ],
        date_from: dayjs(this.dates[0]).format('YYYY-MM-DD'),
        guests,
      }

      const post = await Api.postData(url, data)

      if (post.guests) {
        this.birthdayError = post.guests[0]
      } else {
        this.birthdayError = null
      }
    },

    setFirstTraveller(key, value) {
      // sets data from billing to first traveller if no value is set
      /* istanbul ignore else */
      if (this.fields['$form-1'][`traveller_0_${key}`].untouched) {
        this.setTraveller({ k: key, i: 0, v: value })
        this.$nextTick(() => {
          this.$validator.validate(`form-1.traveller_0_${key}`)
        })
      }
    },

    setCountry(value) {
      this.setBilling({ k: 'country', v: value })
      this.setFirstTraveller('nationality', value)
    },

    getDatepickerOpen(idx) {
      return !!this.datepickersOpen[idx]
    },

    setDatepickerOpen(idx, open) {
      if (this.datepickersOpen[idx] === open) {
        return
      }

      if (!open) {
        // Nur schließen wenn allowClose true ist
        if (this.allowClose[idx]) {
          this.$set(this.datepickersOpen, idx, false)
          this.$set(this.allowClose, idx, false)
        }
      } else {
        this.$set(this.datepickersOpen, idx, open)
      }
    },
    onBirthdateBlur(idx) {
      // Wenn Nutzer tippt und das Feld verlässt, Kalender zuverlässig schließen
      this.$set(this.allowClose, idx, true)
      // console.log('onBirthdateBlur', idx, 'close calender')
      this.setDatepickerOpen(idx, false)
      // Direkt prüfen, falls noch ein debouncter Aufruf aussteht
      if (this._debouncedCheckBirthdates && this._debouncedCheckBirthdates.flush) {
        try {
          this._debouncedCheckBirthdates.flush()
        } catch (e) {
          /* ignore */
        }
      } else {
        this.checkBirthdates()
      }
    },
    ageOnArrival(birthdate) {
      try {
        if (!birthdate || !this.dates || !this.dates.length) return null
        const arrival = dayjs(this.dates[0])
        const bday = dayjs(birthdate)
        if (!arrival.isValid() || !bday.isValid()) return null
        const years = arrival.diff(bday, 'year')
        return years >= 0 ? years : null
      } catch (e) {
        return null
      }
    },

    personNameForBirthdate(birthdate) {
      try {
        const years = this.ageOnArrival(birthdate)
        if (years === null) return null
        const persons = this.campground && this.campground.persons ? this.campground.persons : []
        if (!persons || !persons.length) return null

        // Filter out pets and pick matching age range (inclusive)
        const candidates = persons
          .filter(p => p && p.person_type !== 'pet')
          .filter(p => {
            const from = typeof p.age_from === 'number' ? p.age_from : -Infinity
            const to = typeof p.age_to === 'number' ? p.age_to : Infinity
            return years >= from && years <= to
          })

        if (!candidates.length) return null

        // Prefer the most specific (smallest range) if multiple match
        candidates.sort((a, b) => {
          const aFrom = typeof a.age_from === 'number' ? a.age_from : -Infinity
          const aTo = typeof a.age_to === 'number' ? a.age_to : Infinity
          const bFrom = typeof b.age_from === 'number' ? b.age_from : -Infinity
          const bTo = typeof b.age_to === 'number' ? b.age_to : Infinity
          const aRange = aTo - aFrom
          const bRange = bTo - bFrom
          return aRange - bRange
        })

        return candidates[0].name || null
      } catch (e) {
        return null
      }
    },

    back(formScope) {
      if (formScope === 'form-1') {
        this.$router.push({
          name: 'Step',
          params: { ...this.$route.params, step: this.activeStep - 1 },
        })
      } else {
        this.setStepStatus({ number: 4, key: 'formPart1Passed', value: false })
        window.scrollTo(0, 0)
      }
    },

    next(formScope) {
      // in case of form-1 and billing_mail_repeat has not been touched, set it to touched,
      // otherwise no error message will be shown
      /* istanbul ignore else */
      if (formScope === 'form-1' && !this.fields['$form-1'].billing_mail_repeat.touched) {
        this.$validator.flag('form-1.billing_mail_repeat', { touched: true })
      }

      return this.$validator.validateAll(formScope).then(result => {
        /* istanbul ignore else */
        if (!result && this.$el.querySelector('.form-group.error')) {
          this.$el.querySelector('.form-group.error').scrollIntoView()
        }

        if (!result) {
          let errors = []
          Object.values(this.$validator.errors.items).forEach(item => {
            errors.push(item.field)
          })
        }

        /* istanbul ignore else */
        if (result && formScope === 'form-1') {
          this.setStepStatus({ number: 4, key: 'formPart1Passed', value: result })
          window.scrollTo(0, 0)
        }

        /* istanbul ignore else */
        if (result && formScope === 'form-2') {
          this.setStepStatus({ number: 4, key: 'fulfilled', value: true })
          this.toggleBookingDetails()
        }
      })
    },

    datepickerI18n() {
      return {
        nights: [this.$tc('generic.date.nights', 1), this.$tc('generic.date.nights', 2)],
        until: this.$t('generic.until'),
        today: this.$t('generic.today'),
        inputFormat: this.$t('generic.date.birthdateInputFormat'),
        placeholder: this.$t('generic.date.birthdatePlaceholder'),
        inputPlaceholder: this.$t('generic.date.birthdateInputPlaceholder'),
      }
    },

    onFormValidChange(newValue) {
      /* istanbul ignore else */
      if (!newValue) {
        this.setStepStatus({ number: 4, key: 'fulfilled', value: false })
      }
    },

    trackStep() {
      /*
      let stage
      if (this.showBookingDetails) {
        stage = 'recheck_booking'
      } else {
        stage = this.steps[4].formPart1Passed ? 'address' : 'personal_data'
      }
       */
    },

    setStepStatus(params) {
      const oldFormPart1Passed = this.steps[4].formPart1Passed
      this.setApplicationStepStatus(params)
      if (params.key === 'formPart1Passed' && params.value !== oldFormPart1Passed) {
        this.trackStep()
      }
    },
  },
  watch: {
    formValid: {
      handler: 'onFormValidChange',
    },
    // When billing mail values are set from store (e.g., draft prefill),
    // re-validate to keep Vee-Validate's internal value in sync for `confirmed`
    'billing.mail'() {
      // Do not mirror into mail_repeat; just (re)validate
      this.$nextTick(() => {
        this.$validator.validate('form-1.billing_mail')
        // If repeat currently has a value, validate it as well to update confirmed rule
        if (this.billing && this.billing.mail_repeat !== undefined) {
          this.$validator.validate('form-1.billing_mail_repeat')
        }
      })
    },
    'billing.mail_repeat'() {
      this.$nextTick(() => {
        this.$validator.validate('form-1.billing_mail_repeat')
      })
    },
    'billing.name'(val) {
      if (process.env.VUE_APP_ENVNAME !== 'prod' && val && val.toLowerCase() === 'cnctest') {
        this.setBilling({ k: 'name', v: 'John' })
        this.setBilling({ k: 'lastname', v: 'Doe' })
        this.setBilling({ k: 'mail', v: 'tools+checkouttest@campnerd.de' })
        this.setBilling({ k: 'mail_repeat', v: 'tools+checkouttest@campnerd.de' })
        this.setBilling({ k: 'phone', v: '+49123456789' })
        Object.keys(this.travellers).forEach(index => {
          this.setTraveller({ k: 'first_name', i: index, v: 'John' })
          this.setTraveller({ k: 'last_name', i: index, v: 'Doe' })
          this.setTraveller({ k: 'birthdate', i: index, v: '1990-03-15' })
        })
        this.setCar({ k: 'license_plate', v: 'B 1234' })
        if (this.showCarDetails) {
          this.setCar({ k: 'length', v: '6' })
          this.setCar({ k: 'width', v: '3' })
        }
        this.setBilling({ k: 'street', v: 'Hauptstraße 25' })
        this.setBilling({ k: 'street_additional', v: 'Apartment 3B' })
        this.setBilling({ k: 'zip', v: '10115' })
        this.setBilling({ k: 'city', v: 'Berlin' })
        this.setBilling({ k: 'country', v: { code: 'de', label: 'Germany' } })
        this.setBilling({ k: 'message', v: 'Hello, this is a message from the guest' })
      }
    },
    showBookingDetails() {
      this.trackStep()
    },
  },
  created() {
    this.trackStep()
    // debounce für Birthdate-Checks, um Anfragen bei schneller Eingabe zu reduzieren
    // 400ms hat sich als guter Kompromiss erwiesen
    this._debouncedCheckBirthdates = debounce(() => {
      this.checkBirthdates()
    }, 400)
  },
}
</script>

<style lang="scss" scoped>
/* Smaller, subtle styling for age + person label next to birthdate */
.age-on-arrival {
  font-size: 0.85em;
  opacity: 0.8;
}

.traveller-separator {
  border: 0;
  border-top: 1px solid var(--secondary-color);
  margin: 20px 0;
}
</style>
<style lang="scss" scoped>
.checkout-step-four__header {
  margin: 0 0 30px;
}

.checkout-step-four__headline {
  @include font-format(1.375rem, 1.625em);
}

.checkout-step-four__copy {
  margin: 0;

  &.checkout-step-four__copy--small {
    @include font-format(0.625em, 0.875em);
  }
}

.checkout-step-four__navigation {
  display: flex;
}

.suffix-wrapper {
  position: relative;

  input[type='number'] {
    -moz-appearance: textfield;
  }

  input[type='number']::-webkit-outer-spin-button,
  input[type='number']::-webkit-inner-spin-button {
    appearance: none;
    margin: 0;
  }

  &::after {
    position: absolute;
    top: 50%;
    transform: translate(0, -50%);
    right: 15px;
    content: 'm';
    opacity: 0.6;
  }
}

.birthday-error {
  font-size: 0.85em;
  color: $primary-red;
}
</style>
